import type { ThemeSettingsState } from '@sites/site-theme/src/state/theme.types';
import type {
    ThemeButtonPreset,
    ThemeColorPreset,
    ThemeFontPreset,
    ThemeSelectedButtons,
    ThemeSelectedColors,
    ThemeSelectedFonts,
} from '@sites/site-theme/src/types';

import { getThemeButtonPresets } from '@sites/site-theme/src/config/presets/button-presets.model';
import { getThemeColorPresets } from '@sites/site-theme/src/config/presets/color-presets.model';
import { getThemeFontPresets } from '@sites/site-theme/src/config/presets/font-presets.model';

/**
 * Picking a starting selection, and keeping the preset strip honest about a custom one.
 *
 * These were methods on `AbstractBaseThemeStrategy`, an abstract class with a single implementation
 * whose only override was `adaptThemeSettings`. As free functions they say the same thing without a
 * class, an injection token and a `forRoot` to hand one over.
 */

/** The selection a page falls back to when it has none stored — the first shipped preset. */
export function selectColors(colors?: ThemeSelectedColors): ThemeSelectedColors {
    if (colors?.selected) {
        return { selected: colors.selected, selectedIndex: colors.selectedIndex };
    }

    const [preset] = getThemeColorPresets();

    return { selected: preset!.values, selectedIndex: preset!.index };
}

export function selectFonts(fonts?: ThemeSelectedFonts): ThemeSelectedFonts {
    const syncWithDesktop = fonts?.syncWithDesktop ?? true;

    if (fonts?.selected) {
        return { selected: withEveryTag(fonts.selected), selectedIndex: fonts.selectedIndex, syncWithDesktop };
    }

    const [preset] = getThemeFontPresets();

    return { selected: preset!.values, selectedIndex: preset!.index, syncWithDesktop };
}

/**
 * One font per tag the settings panel lists, in the panel's order.
 *
 * A theme saved before a tag existed has no row for it, and the panel binds a form control per tag
 * — so a missing one is an empty row the user cannot fill. Filling it from the first shipped preset
 * is what the old `adaptThemeSettings` did on every page load; here it is a display concern, and it
 * stays one: a tag the user HAS set is taken exactly as stored, never merged with the preset.
 *
 * Deliberately not part of the migration. It depends on which tags this build ships, so its answer
 * changes when that list does — a migration whose output moves under it is not a migration.
 */
function withEveryTag(selected: ThemeSelectedFonts['selected']): ThemeSelectedFonts['selected'] {
    const [preset] = getThemeFontPresets();
    const stored = new Map(selected.map((font) => [font.tag, font]));

    return preset!.values.map((modelFont) => stored.get(modelFont.tag) ?? modelFont);
}

export function selectButtons(buttons?: ThemeSelectedButtons): ThemeSelectedButtons {
    if (buttons?.selected) {
        return { selected: buttons.selected, selectedIndex: buttons.selectedIndex };
    }

    const [preset] = getThemeButtonPresets();

    return { selected: preset!.values, selectedIndex: preset!.index };
}

/**
 * The shipped presets, with the current selection prepended as a nameless one when it matches none.
 *
 * That extra entry is what makes an edited theme show up in the strip as its own swatch: an
 * `index` of `null` says "this is yours", and clicking any real preset replaces it.
 */
export function withCustomColorPreset(colors: ThemeSelectedColors): ThemeColorPreset[] {
    const presets = getThemeColorPresets();

    return presets.some((preset) => preset.index === colors.selectedIndex)
        ? presets
        : [{ index: null!, values: colors.selected }, ...presets];
}

export function withCustomFontPreset(fonts: ThemeSelectedFonts): ThemeFontPreset[] {
    const presets = getThemeFontPresets();

    return presets.some((preset) => preset.index === fonts.selectedIndex)
        ? presets
        : [{ index: null!, values: fonts.selected }, ...presets];
}

export function withCustomButtonPreset(buttons: ThemeSelectedButtons): ThemeButtonPreset[] {
    const presets = getThemeButtonPresets();

    return presets.some((preset) => preset.index === buttons.selectedIndex)
        ? presets
        : [{ index: null!, values: buttons.selected }, ...presets];
}

/**
 * A whole theme from the shipped presets — what `websites-builder` falls back to when a stored one
 * cannot be formatted.
 */
export function defaultThemeSettings(): ThemeSettingsState {
    return { colors: selectColors(), fonts: selectFonts(), buttons: selectButtons() };
}
